import { useEffect, useState } from 'react'
import type { Attempt, AttemptUpdate } from './types'
import { loadAttempts, saveAttempts, updateStoredAttempt } from './lib/storage'
import { scheduleReview } from './lib/learning'
import { getCurrentUser, onAuthChange, signOutCloud } from './lib/auth'
import { attemptRepository, profileRepository } from './lib/repositories'
import { isCloudflarePracticeConfigured, listHostedAttempts } from './lib/generatorApi'
import { Home } from './pages/Home'
import { Practice } from './pages/Practice'
import { Dashboard } from './pages/Dashboard'
import { Exam } from './pages/Exam'
import { Arena } from './pages/Arena'
import { Admin } from './pages/Admin'
import { AuthModal, UserProfile } from './components/AuthModal'

function getInitialRoute() {
  const hash = location.hash.replace(/^#\/?/, '')
  const path = location.pathname.replace(/^\//, '')
  return (hash || path || 'home').split('?')[0]
}

function loadLocalUser(): UserProfile | null {
  try {
    const saved = localStorage.getItem('sangmath_user')
    if (!saved) return null
    const user = JSON.parse(saved)
    return {
      uid: String(user.uid || `demo-${Date.now()}`),
      displayName: String(user.displayName || 'Học sinh demo'),
      nickname: String(user.nickname || user.displayName || 'Học sinh demo'),
      avatar: String(user.avatar || '🧠'),
      email: String(user.email || ''),
      provider: user.provider === 'supabase' || user.provider === 'firebase' ? user.provider : 'demo',
      role: user.role === 'admin' || user.role === 'teacher' || user.role === 'parent' ? user.role : 'student',
    }
  } catch { return null }
}

export default function App() {
  const [route, setRoute] = useState<string>(getInitialRoute)
  const [attempts, setAttemptsState] = useState<Attempt[]>(loadAttempts())
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [user, setUser] = useState<UserProfile | null>(loadLocalUser)
  const [isAuthOpen, setIsAuthOpen] = useState(false)

  const handleLogin = (u: UserProfile) => {
    setUser(u)
    localStorage.setItem('sangmath_user', JSON.stringify(u))
    void profileRepository.upsert(u).catch(error => console.warn('Không thể đồng bộ hồ sơ:', error))
  }

  const handleLogout = async () => {
    setUser(null)
    localStorage.removeItem('sangmath_user')
    try { await signOutCloud() } catch (error) { console.warn('Không thể đăng xuất cloud:', error) }
  }

  const go = (x: string) => {
    if (location.hash) {
      location.hash = `#/${x}`
    } else {
      history.pushState(null, '', `/${x}`)
    }
    setRoute(x)
    setIsMenuOpen(false)
    window.scrollTo(0, 0)
  }

  const addAttempt = (attempt: Attempt) => {
    setAttemptsState(current => {
      const nextAttempt = { ...attempt, reviewDueAt: scheduleReview(attempt, current) }
      const next = [...current, nextAttempt]
      saveAttempts(next)
      if (user?.provider === 'supabase' && !nextAttempt.serverPersisted) void attemptRepository.save(user.uid, nextAttempt).catch(error => console.warn('Không thể đồng bộ lượt làm:', error))
      return next
    })
  }

  const updateAttempt = (id: string, update: AttemptUpdate) => {
    setAttemptsState(current => {
      const next = updateStoredAttempt(current, id, update)
      saveAttempts(next)
      if (user?.provider === 'supabase') void attemptRepository.update(user.uid, id, update).catch(error => console.warn('Không thể đồng bộ phản tư:', error))
      return next
    })
  }

  useEffect(() => {
    const handleLocationChange = () => {
      setRoute(getInitialRoute())
    }
    addEventListener('hashchange', handleLocationChange)
    addEventListener('popstate', handleLocationChange)
    return () => {
      removeEventListener('hashchange', handleLocationChange)
      removeEventListener('popstate', handleLocationChange)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void getCurrentUser().then(cloudUser => {
      if (cancelled || !cloudUser) return
      handleLogin(cloudUser)
    }).catch(error => console.warn('Không thể khôi phục phiên đăng nhập:', error))
    return onAuthChange(cloudUser => {
      if (cancelled) return
      if (cloudUser) handleLogin(cloudUser)
      else setUser(current => {
        if (current?.provider !== 'supabase' && current?.provider !== 'firebase') return current
        localStorage.removeItem('sangmath_user')
        return null
      })
    })
  }, []) // Supabase owns cloud session changes; local-demo never enters this branch.

  useEffect(() => {
    if (user?.provider !== 'supabase') return
    let cancelled = false
    void attemptRepository.list(user.uid).then(cloudAttempts => {
      if (cancelled || !cloudAttempts) return
      setAttemptsState(localAttempts => {
        const merged = new Map([...cloudAttempts, ...localAttempts].map(attempt => [attempt.id, attempt]))
        const next = [...merged.values()].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
        saveAttempts(next)
        const cloudIds = new Set(cloudAttempts.map(attempt => attempt.id))
        next.filter(attempt => !cloudIds.has(attempt.id)).forEach(attempt => {
          void attemptRepository.save(user.uid, attempt).catch(error => console.warn('Không thể chuyển lượt local lên cloud:', error))
        })
        return next
      })
    }).catch(error => console.warn('Không thể tải lịch sử cloud:', error))
    return () => { cancelled = true }
  }, [user?.uid, user?.provider])

  useEffect(() => {
    if (user?.provider !== 'firebase' || !isCloudflarePracticeConfigured()) return
    let cancelled = false
    void listHostedAttempts().then(cloudAttempts => {
      if (cancelled) return
      setAttemptsState(localAttempts => {
        const merged = new Map([...localAttempts, ...cloudAttempts].map(attempt => [attempt.id, attempt]))
        const next = [...merged.values()].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
        saveAttempts(next)
        return next
      })
    }).catch(error => console.warn('Không thể tải lịch sử Cloudflare:', error))
    return () => { cancelled = true }
  }, [user?.uid, user?.provider])

  const renderPage = () => {
    switch (route) {
      case 'practice':
        return <Practice attempts={attempts} addAttempt={addAttempt} updateAttempt={updateAttempt} go={go} user={user} />
      case 'dashboard':
        return <Dashboard attempts={attempts} go={go} />
      case 'exam':
        return <Exam attempts={attempts} addAttempt={addAttempt} updateAttempt={updateAttempt} go={go} />
      case 'arena':
        return <Arena go={go} user={user} openAuth={() => setIsAuthOpen(true)} addAttempt={addAttempt} updateAttempt={updateAttempt} />
      case 'admin':
        return <Admin user={user} go={go} openAuth={() => setIsAuthOpen(true)} />
      case 'home':
      default:
        return <Home go={go} />
    }
  }

  const isExamOrPractice = route === 'practice' || route === 'exam'
  const isAdminUser = user?.role === 'admin'

  return (
    <>
      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => setIsAuthOpen(false)} 
        user={user} 
        onLogin={handleLogin} 
        onLogout={handleLogout} 
      />

      {!isExamOrPractice && (
        <header className="topbar">
          <div className="flex items-center gap-2">
            <button 
              className="mobile-hamburger-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              title="Menu"
            >
              {isMenuOpen ? '✕' : '☰'}
            </button>
            <button className="brand" onClick={() => go('home')}>
              <svg width="32" height="32" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="mathGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f97316"/>
                    <stop offset="50%" stopColor="#fbbf24"/>
                    <stop offset="100%" stopColor="#22c55e"/>
                  </linearGradient>
                </defs>
                <rect width="256" height="256" rx="64" fill="url(#mathGrad)" />
                <text x="128" y="142" font-family="system-ui, -apple-system, sans-serif" font-weight="900" font-size="160" text-anchor="middle" dominant-baseline="middle" fill="#ffffff">S</text>
              </svg>
              <span>Math</span>
              <em>ĐGNL</em>
            </button>
          </div>
          
          <nav className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
            <button className={`nav-item ${route === 'home' ? 'active' : ''}`} onClick={() => go('home')}>Trang chủ</button>
            <button className={`nav-item ${route === 'practice' ? 'active' : ''}`} onClick={() => go('practice')}>Luyện tập</button>
            <button className={`nav-item ${route === 'exam' ? 'active' : ''}`} onClick={() => go('exam')}>Thi thử</button>
            <button className={`nav-item ${route === 'arena' ? 'active' : ''}`} onClick={() => go('arena')}>🥊 Đấu trường 1v1</button>
            <button className={`nav-item ${route === 'dashboard' ? 'active' : ''}`} onClick={() => go('dashboard')}>Báo cáo năng lực</button>
            
            {isAdminUser && (
              <button className={`nav-item ${route === 'admin' ? 'active' : ''}`} onClick={() => go('admin')}>🛠️ Admin Studio</button>
            )}
          </nav>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button 
              className="btn btn-primary auth-topbar-btn" 
              onClick={(e) => {
                e.stopPropagation()
                setIsAuthOpen(true)
              }}
              style={{ fontSize: '0.9rem', padding: '0.5rem 1.1rem', borderRadius: '100px', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontWeight: 900, background: 'linear-gradient(135deg, var(--orange-primary), #d97706)', color: '#ffffff', cursor: 'pointer', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.35)', border: 'none' }}
            >
              {user ? `👤 ${user.nickname || user.displayName.split(' ')[0]}` : '🔑 ĐĂNG NHẬP'}
            </button>
          </div>
        </header>
      )}
      
      <main className={`main-content ${isExamOrPractice ? 'no-topbar' : ''}`}>
        {renderPage()}
      </main>
    </>
  )
}
